import { DAILY_DEPARTMENTS, DAILY_PRIORITIES, parseDailyTask } from '@felix/domain';
import {
  Badge,
  Button,
  Drawer,
  Modal,
  Select,
  Switch,
  Textarea,
  TextInput,
  UnstyledButton,
} from '@mantine/core';
import { IconExternalLink, IconX } from '@tabler/icons-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatDay } from '../../format.js';
import { DayInput } from '../../inputs/DayInput.js';
import { TimeInput } from '../../inputs/TimeInput.js';
import { StatusBadge } from '../../StatusBadge.js';
import classes from './Daily.module.css';
import { priorityTone, taskBadge } from './logic.js';
import { shipDateTime, timeRange, useErrorText } from './parts.js';
import type { DailyTask, DailyTaskInput } from './types.js';

/** Görev ekleme ve düzeltme (ADR 0051 §4): ortağın taslağındaki alanlar; PMS bağı isteğe bağlı. */
export function TaskDialog({
  opened,
  initial,
  editing,
  workOrders,
  onClose,
  onSave,
}: {
  opened: boolean;
  initial: DailyTaskInput;
  editing: boolean;
  workOrders: readonly { id: string; no: string; title: string }[];
  onClose: () => void;
  onSave: (task: DailyTaskInput) => Promise<void>;
}) {
  const { t } = useTranslation();
  const k = (key: string) => t(`daily.task.${key}`);
  const errorText = useErrorText();
  const [task, setTask] = useState(initial);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [touched, setTouched] = useState(false);
  useEffect(() => {
    if (opened) {
      setTask(initial);
      setError(null);
      setTouched(false);
    }
  }, [opened, initial]);
  const parsed = parseDailyTask(task);
  const issue = (path: string) =>
    touched && !parsed.ok && parsed.issues.some((i) => i.path === path)
      ? path === 'endTime'
        ? t('daily.errors.INVALID_DAILY_TASK')
        : t('daily.form.required')
      : undefined;
  const set = <K extends keyof DailyTaskInput>(key: K, value: DailyTaskInput[K]) =>
    setTask((current) => ({ ...current, [key]: value }));
  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={editing ? k('editTitle') : k('newTitle')}
      size="lg"
      centered
    >
      <form
        className={classes.dialog}
        onSubmit={(event) => {
          event.preventDefault();
          setTouched(true);
          if (!parsed.ok) return;
          setBusy(true);
          setError(null);
          onSave(task)
            .then(onClose)
            .catch((failure: unknown) => setError(errorText(failure)))
            .finally(() => setBusy(false));
        }}
      >
        <div className={classes.formGrid}>
          <DayInput
            label={k('day')}
            value={task.planDay}
            onChange={(v) => set('planDay', v ?? '')}
            required
          />
          <TimeInput
            label={k('start')}
            value={task.startTime}
            onChange={(v) => set('startTime', v)}
            required
          />
          <TimeInput
            label={k('end')}
            value={task.endTime}
            onChange={(v) => set('endTime', v === '' ? null : v)}
            error={issue('endTime')}
          />
          <Select
            label={k('department')}
            data={DAILY_DEPARTMENTS.map((d) => ({ value: d, label: t(`daily.departments.${d}`) }))}
            value={task.department}
            onChange={(v) => v && set('department', v as DailyTaskInput['department'])}
            allowDeselect={false}
            required
          />
          <Select
            label={k('priority')}
            data={DAILY_PRIORITIES.map((p) => ({ value: p, label: t(`daily.priorities.${p}`) }))}
            value={task.priority}
            onChange={(v) => v && set('priority', v as DailyTaskInput['priority'])}
            allowDeselect={false}
            required
          />
          <TextInput
            label={k('responsible')}
            placeholder={k('responsiblePlaceholder')}
            value={task.responsible}
            onChange={(event) => set('responsible', event.currentTarget.value)}
            maxLength={120}
            required
            error={issue('responsible')}
          />
        </div>
        <Textarea
          label={k('description')}
          value={task.description}
          onChange={(event) => set('description', event.currentTarget.value)}
          autosize
          minRows={2}
          maxLength={500}
          required
          error={issue('description')}
        />
        <Select
          label={k('workOrder')}
          placeholder={k('workOrderPlaceholder')}
          data={workOrders.map((w) => ({ value: w.id, label: `${w.no} · ${w.title}` }))}
          value={task.workOrderId}
          onChange={(v) => set('workOrderId', v)}
          searchable
          clearable
        />
        <div className={classes.formGrid}>
          <Textarea
            label={k('risk')}
            value={task.riskNote ?? ''}
            onChange={(event) => set('riskNote', event.currentTarget.value || null)}
            autosize
            minRows={2}
            maxLength={1000}
          />
          <Textarea
            label={k('spares')}
            value={task.sparesTools ?? ''}
            onChange={(event) => set('sparesTools', event.currentTarget.value || null)}
            autosize
            minRows={2}
            maxLength={1000}
          />
        </div>
        <Switch
          label={k('permit')}
          checked={task.permitRequired}
          onChange={(event) => set('permitRequired', event.currentTarget.checked)}
        />
        {error ? (
          <p className={classes.error} role="alert">
            {error}
          </p>
        ) : null}
        <div className={classes.dialogActions}>
          <Button variant="default" onClick={onClose} disabled={busy}>
            {k('cancel')}
          </Button>
          <Button type="submit" loading={busy}>
            {k('save')}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

/** Ortağın "Task Details" paneli: görevin bütün alanları; "View PMS Record" iş emrine götürür. */
export function TaskDetails({
  task,
  tzOffsetMin,
  onClose,
  onOpenWorkOrder,
}: {
  task: DailyTask | null;
  tzOffsetMin: number;
  onClose: () => void;
  onOpenWorkOrder: (id: string) => void;
}) {
  const { t } = useTranslation();
  const k = (key: string, options?: Record<string, unknown>) =>
    t(`daily.details.${key}`, options ?? {});
  const badge = task ? taskBadge(task) : null;
  return (
    <Drawer
      opened={task !== null}
      onClose={onClose}
      position="right"
      size="md"
      withCloseButton={false}
      aria-labelledby="daily-task-title"
    >
      {task && badge ? (
        <div className={classes.details}>
          <div className={classes.detailsHead}>
            <h2 className={classes.detailsLabel}>{k('title')}</h2>
            <StatusBadge tone={badge.tone}>{t(`daily.statuses.${badge.key}`)}</StatusBadge>
            <UnstyledButton
              className={classes.iconButton}
              onClick={onClose}
              aria-label={k('close')}
            >
              <IconX size={18} />
            </UnstyledButton>
          </div>
          <h3 id="daily-task-title" className={classes.detailsTitle}>
            {task.description}
          </h3>
          <Badge variant="light" className={classes.deptBadge} data-department={task.department}>
            {t(`daily.departments.${task.department}`)}
          </Badge>
          <dl className={classes.facts}>
            <dt>{k('dateTime')}</dt>
            <dd>
              {formatDay(task.planDay)} {timeRange(task.startTime, task.endTime)}
            </dd>
            <dt>{k('responsible')}</dt>
            <dd>{task.responsible}</dd>
            <dt>{k('priority')}</dt>
            <dd>
              <StatusBadge tone={priorityTone(task.priority)} withIcon={false}>
                {t(`daily.priorities.${task.priority}`)}
              </StatusBadge>
            </dd>
            <dt>{k('status')}</dt>
            <dd>
              <StatusBadge tone={badge.tone} withIcon={false}>
                {t(`daily.statuses.${badge.key}`)}
              </StatusBadge>
            </dd>
            <dt>{k('permit')}</dt>
            <dd>{task.permitRequired ? k('yes') : k('no')}</dd>
            <dt>{k('risk')}</dt>
            <dd>{task.riskNote ?? k('none')}</dd>
            <dt>{k('spares')}</dt>
            <dd>{task.sparesTools ?? k('none')}</dd>
            <dt>{k('pms')}</dt>
            <dd>{task.workOrder ? `${task.workOrder.no} (${task.workOrder.title})` : k('none')}</dd>
          </dl>
          <div className={classes.noteBox}>
            <h4>{k('description')}</h4>
            <p>{task.description}</p>
            {task.completionNote ? (
              <>
                <h4>{k('notes')}</h4>
                <p>{task.completionNote}</p>
                {task.completedAt ? (
                  <p className={classes.muted}>
                    {k('completedAt', { at: shipDateTime(task.completedAt, tzOffsetMin) })}
                  </p>
                ) : null}
              </>
            ) : null}
            {task.cancelReason ? (
              <>
                <h4>{k('cancelReason')}</h4>
                <p>{task.cancelReason}</p>
              </>
            ) : null}
          </div>
          {task.workOrder ? (
            <div className={classes.detailsActions}>
              <Button
                variant="default"
                leftSection={<IconExternalLink size={16} />}
                onClick={() => task.workOrder && onOpenWorkOrder(task.workOrder.id)}
              >
                {k('viewPms')}
              </Button>
            </div>
          ) : null}
        </div>
      ) : null}
    </Drawer>
  );
}
